import React from 'react';
import PropTypes from 'prop-types';

import {Box, IconButton, Stack, Typography} from 'views/components';
import {DeleteOutline} from 'views/icons';
import {Translate} from 'views/module';
import {AssigneeBadge} from '../../../components';

const PlaybooksStepsPreview = (props) => {
  const {
    steps = [],
    defaultDay,
    setDefaultDay,
    setSteps,
  } = props;

  const onSelectAdmin = (stepIndex, ownerRn, admin) => {
    setSteps((currentSteps) => currentSteps.map((step, index) => {
      if (index !== stepIndex) {
        return step;
      }

      if (!ownerRn) {
        const nextOwnerRns = [...new Set([...(step.ownerRns || []), admin.userRn])];

        return {
          ...step,
          ownerRns: nextOwnerRns,
        };
      }

      return {
        ...step,
        ownerRns: (step.ownerRns || []).map((currentOwnerRn) => (
          currentOwnerRn === ownerRn ? admin.userRn : currentOwnerRn
        )),
      };
    }));
  };

  const onUnassign = (stepIndex, ownerRn) => {
    setSteps((currentSteps) => currentSteps.map((step, index) => {
      if (index !== stepIndex) {
        return step;
      }

      return {
        ...step,
        ownerRns: (step.ownerRns || []).filter((currentOwnerRn) => currentOwnerRn !== ownerRn),
      };
    }));
  };

  const onDeleteStep = (stepIndex) => {
    setSteps((currentSteps) => {
      const nextSteps = currentSteps.filter((_step, index) => index !== stepIndex);
      const hasCurrentDefaultDay = nextSteps.some((step) => step.day === defaultDay);

      if (!hasCurrentDefaultDay) {
        const nextHighestDay = nextSteps.reduce((highestDay, step) => Math.max(highestDay, step?.day || 1), 1);
        setDefaultDay(nextHighestDay);
      }

      return nextSteps;
    });
  };

  return (
    <Box
      sx={{
        borderRadius: '12px',
        px: '14px',
        py: '12px',
      }}
    >
      {
        steps.map((step, index) => {
          const showsDayLabel = index === 0 || steps[index - 1].day !== step.day;
          const isLastStepInDayGroup = index === steps.length - 1 ||
            steps[index + 1].day !== step.day;
          const continuesToNextStep = step.day !== defaultDay &&
            index < steps.length - 1 &&
            steps[index + 1].day === step.day;

          return (
            <Stack
              direction='row'
              alignItems='flex-start'
              sx={{
                'gap': '16px',
                'mb': isLastStepInDayGroup ? '10px' : '5px',
                '&:hover .playbook-step-delete': {
                  opacity: 1,
                },
              }}
              key={step.stepRn || `${step.prompt || 'step'}-${index}`}
            >
              <Box
                sx={{
                  width: '56px',
                  flexShrink: 0,
                  position: 'relative',
                  minHeight: '24px',
                }}
              >
                {
                  showsDayLabel &&
                  <Typography
                    sx={{
                      fontWeight: 500,
                      color: '#A8A49E',
                      lineHeight: '150%',
                      fontSize: '13px',
                      mb: 0,
                    }}
                  >
                    <Translate expression='playbooks.New.StepPreview.Day' substitute={{day: step.day}} />
                  </Typography>
                }
                {
                  continuesToNextStep &&
                  <Box
                    sx={{
                      position: 'absolute',
                      top: showsDayLabel ? '22px' : 0,
                      bottom: '-16px',
                      left: '15px',
                      width: '1px',
                      backgroundColor: '#E0DBD3',
                    }}
                  />
                }
              </Box>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#1A1916',
                  lineHeight: '150%',
                  flex: 1,
                }}
              >
                {'\u2022'} {step.prompt}
              </Typography>
              <Stack
                direction='row'
                sx={{
                  alignItems: 'center',
                  flexShrink: 0,
                  spacing: 0,
                  gap: '8px',
                }}
              >
                {
                  [...((step.ownerRns && step.ownerRns.length > 0) ? step.ownerRns : []), null].map((ownerRn, ownerIndex) => (
                    <AssigneeBadge
                      key={`${step.stepRn || index}-${ownerRn || 'unassigned'}`}
                      user={ownerRn ? {userRn: ownerRn} : {}}
                      sx={{
                        width: '24px',
                        height: '24px',
                        position: 'relative',
                        zIndex: ownerIndex + 1,
                        fontSize: '11px',
                        outline: '2px solid',
                        outlineColor: '#FFFFFF',
                        ml: ownerIndex === 0 ? 0 : '-6px',
                      }}
                      onSelectAdmin={(admin) => onSelectAdmin(index, ownerRn, admin)}
                      onUnassign={ownerRn ? () => onUnassign(index, ownerRn) : undefined}
                    />
                  ))
                }
                <IconButton
                  className='playbook-step-delete'
                  onClick={() => onDeleteStep(index)}
                  sx={{
                    'width': '24px',
                    'height': '24px',
                    'color': '#A8A49E',
                    'opacity': 0,
                    'transition': 'opacity 0.15s ease',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#6B6760',
                    },
                  }}
                >
                  <DeleteOutline sx={{fontSize: '16px'}} />
                </IconButton>
              </Stack>
            </Stack>
          );
        })
      }
    </Box>
  );
};

PlaybooksStepsPreview.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({
    ownerRns: PropTypes.arrayOf(PropTypes.string),
    prompt: PropTypes.string,
    stepRn: PropTypes.string,
  })),
  defaultDay: PropTypes.number.isRequired,
  setDefaultDay: PropTypes.func.isRequired,
  setSteps: PropTypes.func.isRequired,
};

export default React.memo(PlaybooksStepsPreview);
